<template>
  <BaseCard
    :result="{
      id: aiData.id,
      type: 'ai_search_response',
      result_position: 0,
      display_text: aiData.answer,
      category: 'ai_search_overview',
    }"
    :is-content-loading="aiData.isLoading || isStreaming"
    class="ai-answer-card result-card"
    @open-feedback-modal="$emit('openFeedbackModal')"
  >
    <template #tags>
      <span class="status-tag">
        <Icon
          name="lucide:sparkles"
          size="10"
        />
        {{ statusText }}
      </span>
      <span class="date-tag">
        {{ dateText }}
      </span>
    </template>

    <template #likes-section>
      <button
        v-if="showContinueInChat"
        type="button"
        class="continue-in-chat-btn"
        :aria-label="$t('search.aiAnswer.continueInChatAriaLabel')"
        @click.stop="handleContinueInChat"
      >
        <span>{{ $t('search.aiAnswer.continueInChat') }}</span>
        <Icon name="lucide:arrow-right" />
      </button>
    </template>

    <template #result-content>
      <div
        v-if="aiData.isLoading"
        class="skeleton-loader-container"
      >
        <div class="answer">
          <SkeletonLoader
            width="100%"
            height="14px"
          />
          <SkeletonLoader
            width="60%"
            height="14px"
          />
          <SkeletonLoader
            width="70%"
            height="14px"
          />
        </div>
        <div class="sources">
          <SkeletonLoader
            width="80px"
            height="16px"
            border-radius="99px"
          />
          <SkeletonLoader
            width="120px"
            height="16px"
            border-radius="99px"
          />
        </div>
      </div>
      <div
        v-else
        class="content"
        data-testid="ai-card-content"
      >
        <div
          ref="answerRef"
          class="answer-wrapper"
          :class="{ 'has-max-height': (enableShowMoreButton || isStreaming) && !showMore }"
          :style="{ height: showMore ? 'auto' : enableShowMoreButton || isStreaming ? '122px' : 'auto' }"
        >
          <div
            class="answer"
            v-html="displayedContent"
          />
          <AiAnswerCardSources
            v-if="!isStreaming && aiData.references.length > 0"
            :sources="aiData.references"
          />
        </div>
        <div
          v-if="enableShowMoreButton"
          class="show-more-btn"
        >
          <button
            type="button"
            class="show-more-btn-btn"
            :aria-expanded="showMore"
            @click.stop="handleShowMore"
          >
            {{ showMore ? $t('search.aiAnswer.showLessBtn') : $t('search.aiAnswer.showMoreBtn') }}
            <Icon
              size="18"
              :name="showMore ? 'lucide:chevron-up' : 'lucide:chevron-down'"
            />
          </button>
        </div>
      </div>
    </template>
  </BaseCard>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick, useTemplateRef } from 'vue';
import BaseCard from './BaseCard.vue';
import SkeletonLoader from '~/components/global/SkeletonLoader.vue';
import AiAnswerCardSources from './AiAnswerCardSources.vue';
import { useAiChatWidget } from '~/composables/useAiChatWidget';

const props = defineProps({
  aiData: {
    type: Object,
    default: () => ({
      answer: '',
      references: [],
      status: '',
      isLoading: false,
    }),
  },
});

const { $t } = useNuxtApp();
const emit = defineEmits(['continueInChat', 'openFeedbackModal']);

const { aiChatEnabled } = useAiChatWidget();
const showContinueInChat = computed(() => aiChatEnabled.value && !isStreaming.value);
const handleContinueInChat = () => {
  emit('continueInChat');
};

const displayedContent = ref('');
const isStreaming = ref(false);
const enableShowMoreButton = ref(false);
const showMore = ref(false);
const answerRef = useTemplateRef('answerRef');

const dateText = computed(() => {
  if (props.aiData.isLoading || isStreaming.value) {
    return '';
  }

  return new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
});

const statusText = computed(() => {
  if (props.aiData.isLoading) {
    return `${$t('search.aiAnswer.thinking')}...`;
  }
  if (isStreaming.value) {
    return `${$t('search.aiAnswer.generating')}...`;
  }
  return $t('search.aiAnswer.overview');
});

const handleShowMore = () => {
  showMore.value = !showMore.value;
};

const checkContentOverflow = async () => {
  await nextTick();
  if (answerRef.value) {
    const hasOverflow = answerRef.value.scrollHeight > 130;

    if (hasOverflow && !enableShowMoreButton.value) {
      enableShowMoreButton.value = true;
    } else if (!hasOverflow && enableShowMoreButton.value) {
      enableShowMoreButton.value = false;
      showMore.value = false;
    }
  }
};

const streamHtml = (htmlString, displayedHtmlRef) => {
  const tokens = htmlString
    .trim()
    .split(/(\s+|<[^>]*>)/)
    .filter(Boolean);
  let i = 0;

  isStreaming.value = true;

  function nextToken() {
    if (i < tokens.length) {
      displayedHtmlRef.value += tokens[i];
      i++;
      requestAnimationFrame(nextToken);
    } else {
      isStreaming.value = false;
    }
  }
  nextToken();
};

watch(
  () => props.aiData.answer,
  (newAnswer, oldAnswer) => {
    // Allow initial run when oldAnswer is undefined but newAnswer has content
    // or normal runs when oldAnswer exists and values are different
    if ((oldAnswer === undefined && newAnswer) || (oldAnswer !== undefined && newAnswer !== oldAnswer)) {
      displayedContent.value = '';
      streamHtml(newAnswer, displayedContent);
    }
  },
  { immediate: true },
);

watch(isStreaming, async (newValue) => {
  if (!newValue) {
    await checkContentOverflow();
  }
});

watch(displayedContent, async () => {
  if (isStreaming.value) {
    await checkContentOverflow();
  }
});

onMounted(() => {
  checkContentOverflow();
});
</script>

<style scoped>
.ai-answer-card {
  --ai-color: linear-gradient(145deg, #1d62ee 9.46%, #7a14ef 106.88%);
  box-sizing: border-box;
  border: 1px solid transparent;
  /* Technique to create a gradient border using multiple backgrounds with padding-box and border-box */
  background:
    linear-gradient(var(--color-blue-50), var(--color-blue-50)) padding-box,
    var(--ai-color) border-box;

  .date-tag {
    font-size: 12px;
    font-weight: 400;
    line-height: 1.3;
    color: var(--color-text-secondary);
  }

  .status-tag {
    display: flex;
    align-items: center;
    gap: 4px;
    background: var(--ai-color);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.3;
  }

  .content {
    font-size: 14px;
    color: var(--color-text-secondary);
    line-height: 1.5;
    display: flex;
    flex-direction: column;
    gap: 8px;
    white-space: pre-wrap;
  }

  .answer-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;
    transition: height 0.3s ease;

    &.has-max-height {
      max-height: 122px;
    }
  }

  .skeleton-loader-container {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .answer {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .sources {
      display: flex;
      gap: 8px;
    }
  }

  .show-more-btn {
    display: flex;
    justify-content: center;
    margin-top: 8px;
    border-radius: 9999px;
    background-color: #fff;
    border: 1px solid var(--color-blue-300);

    .show-more-btn-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      background: transparent;
      border: none;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      color: var(--color-blue-500);
      padding: 4px 8px;
      transition: opacity 0.2s ease;

      &:hover {
        opacity: 0.8;
      }

      .iconify {
        color: var(--color-blue-500);
      }
    }
  }

  .continue-in-chat-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    background: var(--ai-color);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    padding: 0;
    border: none;
    cursor: pointer;

    .iconify {
      color: var(--color-blue-500);
    }
  }
}
</style>
